/**
 * Unified Loading System - Loader Configuration
 *
 * Default configurations for all operation types.
 * These settings control loader behavior, timing, and messages.
 */

import type {
  OperationConfig,
  LoaderVariant,
  OperationType,
  Tip,
} from '../types';

/**
 * General purpose tips for loading states
 */
const GENERAL_TIPS: Tip[] = [
  {
    title: 'Optimizing Your Resume',
    text: 'Our AI analyzes your resume against job requirements to help you stand out.',
  },
  {
    title: 'ATS Compatibility',
    text: 'Applicant Tracking Systems scan resumes for keywords. We ensure yours matches.',
  },
  {
    title: 'Pro Tip',
    text: 'Tailor your resume for each job application to increase your chances of success.',
  },
];

/**
 * CV Generation specific tips
 */
const CV_GENERATION_TIPS: Tip[] = [
  {
    title: 'Creating Your CV',
    text: 'We are generating a tailored resume based on the job requirements.',
  },
  {
    title: 'Optimizing Keywords',
    text: 'Our AI identifies and includes relevant keywords from the job description.',
  },
  {
    title: 'Formatting',
    text: 'Your resume will be formatted for maximum ATS compatibility.',
  },
  {
    title: 'Pro Tip',
    text: 'Review the generated CV and add specific achievements from your experience.',
  },
];

/**
 * ATS Scoring specific tips
 */
const ATS_SCORING_TIPS: Tip[] = [
  {
    title: 'Analyzing Your Resume',
    text: 'We are comparing your resume against the job requirements.',
  },
  {
    title: 'Keyword Matching',
    text: 'Checking if your resume contains the right keywords and skills.',
  },
  {
    title: 'Score Breakdown',
    text: 'You will receive a detailed score with actionable improvement suggestions.',
  },
  {
    title: 'Pro Tip',
    text: 'A score above 80% means your resume is well-matched for this position.',
  },
];

/**
 * Profile Creation specific tips
 */
const PROFILE_CREATION_TIPS: Tip[] = [
  {
    title: 'Building Your Profile',
    text: 'We are extracting information from your resume to create your profile.',
  },
  {
    title: 'Skill Extraction',
    text: 'Identifying your skills to help match you with relevant opportunities.',
  },
  {
    title: 'Experience Analysis',
    text: 'Organizing your work history for easy editing and updates.',
  },
  {
    title: 'Pro Tip',
    text: 'Keep your profile updated to receive the most accurate job matches.',
  },
];

/**
 * Download specific tips
 */
const DOWNLOAD_TIPS: Tip[] = [
  {
    title: 'Preparing Your Download',
    text: 'Your resume is being formatted and prepared for download.',
  },
  {
    title: 'File Formats',
    text: 'PDF is recommended for most applications. DOCX allows further editing.',
  },
  {
    title: 'Pro Tip',
    text: 'Always save your resume with a professional filename format like "John_Doe_Resume.pdf".',
  },
];

/**
 * Operation configurations by type
 */
export const OPERATION_CONFIGS: Record<OperationType, OperationConfig> = {
  'cv-generation': {
    variant: 'progress',
    defaultDuration: 90000, // 90 seconds (middle of 60-120 range)
    timeoutMs: 180000, // 3 minutes
    defaultMessages: {
      start: 'Starting CV generation...',
      inProgress: 'Generating your optimized CV...',
      complete: 'CV generated successfully!',
    },
    tips: CV_GENERATION_TIPS,
  },

  'ats-scoring': {
    variant: 'steps',
    defaultDuration: 45000, // 45 seconds (middle of 30-60 range)
    timeoutMs: 90000, // 90 seconds
    defaultMessages: {
      start: 'Starting ATS analysis...',
      inProgress: 'Analyzing your resume...',
      complete: 'Analysis complete!',
    },
    tips: ATS_SCORING_TIPS,
  },

  'cv-download': {
    variant: 'inline',
    defaultDuration: 10000, // 10 seconds (middle of 5-15 range)
    timeoutMs: 30000, // 30 seconds
    defaultMessages: {
      start: 'Preparing download...',
      inProgress: 'Downloading...',
      complete: 'Download complete!',
    },
    tips: DOWNLOAD_TIPS,
  },

  'profile-creation': {
    variant: 'progress',
    defaultDuration: 45000, // 45 seconds (middle of 30-60 range)
    timeoutMs: 90000, // 90 seconds
    defaultMessages: {
      start: 'Creating your profile...',
      inProgress: 'Processing your information...',
      complete: 'Profile created successfully!',
    },
    tips: PROFILE_CREATION_TIPS,
  },

  general: {
    variant: 'inline',
    defaultDuration: 5000, // 5 seconds
    timeoutMs: 15000, // 15 seconds
    defaultMessages: {
      start: 'Loading...',
      inProgress: 'Please wait...',
      complete: 'Done!',
    },
    tips: GENERAL_TIPS,
  },
};

/**
 * Get configuration for an operation type
 */
export function getOperationConfig(operationType: OperationType): OperationConfig {
  return OPERATION_CONFIGS[operationType] || OPERATION_CONFIGS.general;
}

/**
 * Get default variant for an operation type
 */
export function getDefaultVariant(operationType: OperationType): LoaderVariant {
  return getOperationConfig(operationType).variant;
}

/**
 * Get default duration for an operation type
 */
export function getDefaultDuration(operationType: OperationType): number {
  return getOperationConfig(operationType).defaultDuration;
}

/**
 * Get tips for an operation type
 */
export function getTips(operationType: OperationType): Tip[] {
  return getOperationConfig(operationType).tips;
}

/**
 * Size configurations in pixels
 */
export const LOADER_SIZES = {
  sm: 16,
  md: 24,
  lg: 32,
  xl: 48,
} as const;

/**
 * Animation durations in milliseconds
 */
export const ANIMATION_DURATIONS = {
  spinner: 1000, // Full rotation
  shimmer: 1500, // Shimmer effect
  fadeIn: 200,
  fadeOut: 150,
  tipRotate: 5000, // Tips rotation interval
} as const;

/**
 * Progress update intervals
 */
export const PROGRESS_UPDATE_INTERVAL = 100; // Update every 100ms

/**
 * Minimum progress increment
 */
export const MIN_PROGRESS_INCREMENT = 0.5; // 0.5%

/**
 * Maximum progress before showing 100%
 * This ensures we don't show 100% before actual completion
 */
export const MAX_DISPLAY_PROGRESS = 95;

/**
 * Timeout warning threshold
 * Show warning when progress exceeds this percentage of expected duration
 */
export const TIMEOUT_WARNING_THRESHOLD = 1.5; // 150% of expected duration
